import { MESSAGE_TYPES, type MessageType } from "../../lib/types";
import { cx } from "../../lib/utils";

const LABELS: Record<MessageType, string> = {
  positive: "Positive",
  negative: "Negative",
  neutral: "Neutral",
  all: "All",
};

interface MessageTypeCheckboxesProps {
  selected: MessageType[];
  onChange: (next: MessageType[]) => void;
  /** Shows the "choose at least one" hint in its error color once a save has
   * been attempted with this row empty — driven by the parent form so the
   * hint doesn't flash red before the user has done anything. */
  showRequiredError?: boolean;
}

// Reusable per-campaign sentiment picker — positive/negative/neutral/all,
// multi-select via plain checkboxes (per spec: "checkbox type of feature
// where we can multi select"). Deliberately generic (no campaign/recipient
// awareness) so it can be reused anywhere else sentiment filtering shows up
// later, e.g. a future inbox filter.
export function MessageTypeCheckboxes({ selected, onChange, showRequiredError }: MessageTypeCheckboxesProps) {
  function toggle(type: MessageType, checked: boolean) {
    onChange(checked ? [...selected, type] : selected.filter((t) => t !== type));
  }

  return (
    <div>
      <div className="flex flex-wrap gap-spacing-base" role="group" aria-label="Message types to notify on">
        {MESSAGE_TYPES.map((type) => (
          <label key={type} className="flex items-center gap-spacing-2xs cursor-pointer select-none">
            <input
              type="checkbox"
              checked={selected.includes(type)}
              onChange={(e) => toggle(type, e.target.checked)}
              className="w-4 h-4 rounded accent-primary shrink-0"
            />
            <span className="font-body-sm text-body-sm text-on-surface">{LABELS[type]}</span>
          </label>
        ))}
      </div>
      <p className={cx("font-label-sm text-label-sm mt-spacing-3xs", showRequiredError ? "text-error" : "text-on-surface-variant")}>
        {showRequiredError
          ? "Choose at least one message type to save."
          : selected.length > 0
          ? `${selected.length} message type${selected.length > 1 ? "s" : ""} selected`
          : "Choose at least one message type."}
      </p>
    </div>
  );
}
